import { useEffect, useRef } from 'react';

/**
 * Dismiss + re-anchor wiring for a small dropdown menu WITHOUT a full-screen
 * click-catcher (Finance scroll #500 re-open).
 *
 * Root cause this replaces: every Finance menu opened an invisible
 * `fixed inset-0` backdrop to catch the outside click. That layer also caught
 * every wheel / touch-drag, and since it is not inside any scroller, the page
 * under an open menu could not scroll at all — "the whole screen locks".
 *
 * Instead:
 *  - a `pointerdown` OUTSIDE every node in `refs` closes the menu (capture
 *    phase, so a child's stopPropagation cannot hide it; mouse, pen and touch);
 *  - scroll / resize call `onReflow` (when given) so a viewport-anchored
 *    (portaled, `position: fixed`) menu follows its trigger while the page
 *    scrolls. Scrolls INSIDE the menu itself are ignored (a capped menu
 *    scrolls internally). `onReflow` returning `false` closes the menu
 *    (trigger gone or off screen).
 *
 * `refs` = refs of the trigger and the menu box (a body portal is outside the
 * trigger's subtree, so both must be listed).
 */
export default function useMenuDismiss(open, onClose, refs, onReflow) {
    // Latest callbacks without re-subscribing on every render.
    const closeRef = useRef(onClose);
    const reflowRef = useRef(onReflow);
    const refsRef = useRef(refs);
    closeRef.current = onClose;
    reflowRef.current = onReflow;
    refsRef.current = refs;

    useEffect(() => {
        if (!open) return undefined;
        const inside = (target) => (refsRef.current || []).some(
            (r) => r?.current && target instanceof Node && r.current.contains(target),
        );
        const onDown = (e) => {
            if (inside(e.target)) return;
            closeRef.current?.();
        };
        const onScroll = (e) => {
            if (!reflowRef.current) return;
            if (e?.target && inside(e.target)) return;
            if (reflowRef.current() === false) closeRef.current?.();
        };
        document.addEventListener('pointerdown', onDown, true);
        window.addEventListener('scroll', onScroll, true); // capture: any ancestor scroll
        window.addEventListener('resize', onScroll);
        return () => {
            document.removeEventListener('pointerdown', onDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);
}
